import React, { useLayoutEffect, useRef, useEffect, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import colorPickerIcon from '../../Images/color-picker.svg';
import scaleIcon from '../../Images/scale.png';
import Loader from '../../Components/common/loader/loader';
import './IndividualChart.css';

// Import the SensorData interface from the parent
interface SensorData {
  id: string;
  channelId: string;
  sensorName: string;
  unit: string;
  color: string;
  dataPoints: { date: string; value: number }[];
  minValue?: number;
  maxValue?: number;
  assetType?: string;
  // Threshold data
  alarmHData?: { timestamp: string; value: number }[];
  alarmHHData?: { timestamp: string; value: number }[];
  alarmLData?: { timestamp: string; value: number }[];
  alarmLLData?: { timestamp: string; value: number }[];
}

interface IndividualChartProps {
  sensors: SensorData[];
  selectedSensors: string[];
  selectedTimeRange: string;
  customScaling?: {
    [unit: string]: { min: number; max: number }
  } | null;
  uomRanges?: {
    uom: string;
    minValue: number;
    maxValue: number;
  }[];
  hasNoData: boolean;
  initialDataLoadComplete: boolean;
  chartDataLoading: boolean;
  onCustomScalingOpen?: () => void;
}

// Helper function to parse local datetime string without timezone conversion
const parseLocalDateTime = (dateTimeString: string): number => {
  const cleanDateString = dateTimeString.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
  
  const parts = cleanDateString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?/);
  if (parts) {
    const [, year, month, day, hour, minute, second, millisecond = '000'] = parts;
    const date = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second),
      parseInt(millisecond)
    );
    return date.getTime();
  }
  
  return new Date(dateTimeString).getTime();
};

// Helper function to get timezone short name
const getTimezoneShortName = (): string => {
  const timezoneShort = localStorage.getItem('userTimezoneShort');
  return timezoneShort || '';
};

const getDayKey = (date: Date): string => date.toISOString().split("T")[0];

const MEASUREMENT_UNITS = ['°c', '°f', '%', 'ppm', 'mg/l', 'ph', 'mv', 'ma', 'v', 'a', 'w', 'bar', 'psi', 'l/h', 'm³/h', 'gpm'];

const isRelaySensor = (sensor: SensorData): boolean => {
  if (!sensor) return false;

  const hasOnlyBinaryValues = sensor.dataPoints.every(point => point.value === 0 || point.value === 1);
  const hasNoMeasurementUnit = !sensor.unit || !MEASUREMENT_UNITS.includes(sensor.unit.toLowerCase());

  const sensorName = sensor.sensorName.toLowerCase();
  const hasRelayNaming = sensorName.includes('relay') || sensorName.includes('pump') ||
                         sensorName.includes('valve') || sensorName === 'unassigned';

  return (hasOnlyBinaryValues && hasNoMeasurementUnit) || hasRelayNaming;
};

// Function to get date range based on selection with 10-minute buffer
const getDateRange = (selectedTimeRange: string) => {
  const now = new Date();
  let startMs: number;
  let endMs: number = now.getTime();

  // Calculate relative ranges properly
  switch (selectedTimeRange) {
    case 'Last 1 hour':
      startMs = endMs - (1 * 60 * 60 * 1000);
      break;
    case 'Last 2 hours':
      startMs = endMs - (2 * 60 * 60 * 1000);
      break;
    case 'Last 4 hours':
      startMs = endMs - (4 * 60 * 60 * 1000);
      break;
    case 'Last 8 hours':
      startMs = endMs - (8 * 60 * 60 * 1000);
      break;
    case 'Last 24 hours':
      startMs = endMs - (24 * 60 * 60 * 1000);
      break;
    case 'Last 7 days':
      startMs = endMs - (7 * 24 * 60 * 60 * 1000);
      break;
    default:
      startMs = endMs - (1 * 60 * 60 * 1000); // Default to 1 hour
  }

  // Return timestamps directly (without buffer)
  return {
    startTime: startMs,
    endTime: endMs,
  };
};

type ChartDataPoint = { date: number; value: number };

const GAP_THRESHOLD_MS = 5 * 60 * 1000;

const DAY_LABEL_PALETTE = [
  am5.color(0x2563EB),
  am5.color(0x16A34A),
  am5.color(0xDC2626),
  am5.color(0x7C3AED),
  am5.color(0xF59E0B),
  am5.color(0x0EA5E9),
  am5.color(0xFB7185),
  am5.color(0x14B8A6),
  am5.color(0xF97316),
  am5.color(0x22C55E),
  am5.color(0xEF4444),
  am5.color(0x3B82F6),
  am5.color(0x84CC16),
  am5.color(0xA855F7),
  am5.color(0x06B6D4),
  am5.color(0xEAB308),
  am5.color(0x9333EA),
  am5.color(0x6366F1),
  am5.color(0x10B981),
  am5.color(0xFACC15),
  am5.color(0x8B5CF6),
  am5.color(0x4ADE80),
  am5.color(0xF87171),
  am5.color(0x60A5FA),
  am5.color(0xA3E635),
  am5.color(0xC084FC),
  am5.color(0x38BDF8),
  am5.color(0x34D399),
  am5.color(0xFDBA74),
  am5.color(0x818CF8),
  am5.color(0x65A30D),
];

type UnitMinMax = { min: number | ''; max: number | '' };

const getMinMaxForUnitFromRanges = (
  unit: string,
  uomRanges?: IndividualChartProps['uomRanges']
): UnitMinMax => {
  const actualUnit = unit === 'Other' ? '' : unit;

  if (uomRanges) {
    for (let i = 0; i < uomRanges.length; i++) {
      const range = uomRanges[i];
      if (range.uom === actualUnit) {
        return {
          min: Math.floor(range.minValue),
          max: Math.ceil(range.maxValue)
        };
      }
    }
  }

  return { min: '', max: '' };
};

const sortSensorDataPoints = (dataPoints: SensorData['dataPoints']): ChartDataPoint[] => {
  const sorted: ChartDataPoint[] = [];
  for (let i = 0; i < dataPoints.length; i++) {
    const point = dataPoints[i];
    sorted.push({
      date: parseLocalDateTime(point.date),
      value: point.value
    });
  }
  sorted.sort((a, b) => a.date - b.date);
  return sorted;
};

const processDataWithGaps = (sortedData: ChartDataPoint[]): { date: number; value: number | null }[] => {
  const processedData: { date: number; value: number | null }[] = [];

  for (let i = 0; i < sortedData.length; i++) {
    const current = sortedData[i];
    const previous = i > 0 ? sortedData[i - 1] : null;

    if (previous) {
      const diff = current.date - previous.date;
      if (diff > GAP_THRESHOLD_MS) {
        processedData.push({
          date: previous.date + 1,
          value: null
        });
      }
    }
    processedData.push(current);
  }

  return processedData;
};

const filterCleanChartData = (sortedData: ChartDataPoint[]): ChartDataPoint[] => {
  const cleanData: ChartDataPoint[] = [];
  for (let i = 0; i < sortedData.length; i++) {
    const item = sortedData[i];
    if (
      item.value !== null &&
      item.value !== undefined &&
      !isNaN(item.value) &&
      isFinite(item.value)
    ) {
      cleanData.push(item);
    }
  }
  return cleanData;
};

const extractChartDataValues = (cleanData: ChartDataPoint[]): number[] => {
  const values: number[] = [];
  for (let i = 0; i < cleanData.length; i++) {
    values.push(cleanData[i].value);
  }
  return values;
};

const getThresholdValues = (sensor: SensorData): number[] => {
  const thresholdValues: number[] = [];
  if (sensor.alarmHHData && sensor.alarmHHData.length > 0) thresholdValues.push(sensor.alarmHHData[0].value);
  if (sensor.alarmHData && sensor.alarmHData.length > 0) thresholdValues.push(sensor.alarmHData[0].value);
  if (sensor.alarmLData && sensor.alarmLData.length > 0) thresholdValues.push(sensor.alarmLData[0].value);
  if (sensor.alarmLLData && sensor.alarmLLData.length > 0) thresholdValues.push(sensor.alarmLLData[0].value);
  return thresholdValues;
};

const createSeriesBullet = (root: am5.Root): am5.Bullet => {
  const circle = am5.Circle.new(root, {
    radius: 0,
    fill: am5.color('#000'),
    stroke: am5.color('#fff'),
    strokeWidth: 3,
    interactive: true,
    opacity: 0,
  });
  circle.states.create('default', { opacity: 0 });
  circle.states.create('hover', { radius: 6, opacity: 1 });
  return am5.Bullet.new(root, { locationX: 0.5, sprite: circle });
};

const getDayLabelColor = (
  dayColorMap: Record<string, am5.Color>,
  date: Date
): am5.Color => {
  const key = getDayKey(date);
  if (!dayColorMap[key]) {
    dayColorMap[key] = DAY_LABEL_PALETTE[Object.keys(dayColorMap).length % DAY_LABEL_PALETTE.length];
  }
  return dayColorMap[key];
};

const setupXAxisDayLabelColors = (xAxis: am5xy.DateAxis<am5xy.AxisRenderer>): void => {
  const dayColorMap: Record<string, am5.Color> = {};
  const xRenderer = xAxis.get('renderer');
  xRenderer.labels.template.adapters.add('fill', (fill, label) => {
    const dataItem = label.dataItem;
    if (!dataItem) return fill;

    const value = dataItem.get('value' as any);
    if (!value) return fill;

    const axis = dataItem.component as am5xy.DateAxis<am5xy.AxisRendererX>;
    const dataItems = axis.dataItems;
    const currentIndex = dataItems.indexOf(dataItem);

    if (currentIndex <= 0) {
      return getDayLabelColor(dayColorMap, new Date(value as number));
    }

    const prevDataItem = dataItems[currentIndex - 1];
    const prevValue = prevDataItem?.get('value' as any);
    if (!prevValue) return am5.color(0x9ca3af);

    const d1 = new Date(value as number);
    const d2 = new Date(prevValue as number);

    if (d1.getDate() !== d2.getDate()) {
      return getDayLabelColor(dayColorMap, d1);
    }

    return am5.color(0xffffff);
  });
};

const addThresholdLine = (
  axis: am5xy.ValueAxis<am5xy.AxisRenderer>,
  value: number,
  labelText: string,
  color: am5.Color
): void => {
  const range = axis.createAxisRange(axis.makeDataItem({ value }));
  const grid = range.get('grid');
  if (grid) {
    grid.setAll({
      stroke: color,
      strokeDasharray: [5, 5],
      strokeWidth: 2,
      strokeOpacity: 0.7
    });
  }

  const label = range.get('label');
  if (label) {
    label.setAll({
      text: labelText,
      fill: color,
      fontSize: 10,
      location: 0,
      inside: true,
      centerX: -1030,
      textAlign: 'left',
    });
  }
};

const configureIndividualYAxis = (
  yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
  sensor: SensorData,
  useStepLine: boolean,
  minValue: number,
  maxValue: number,
  individualScaling: { [chartId: string]: { min: number; max: number } },
  customScaling?: IndividualChartProps['customScaling']
): void => {
  const hasIndividualScaling = individualScaling[sensor.id];
  const hasGlobalCustomScaling = customScaling && customScaling[sensor.unit];

  if (hasIndividualScaling) {
    yAxis.set('min', individualScaling[sensor.id].min);
    yAxis.set('max', individualScaling[sensor.id].max);
    yAxis.set('strictMinMax', true);
    yAxis.get('renderer').setAll({ minGridDistance: 40 });
    return;
  }

  if (hasGlobalCustomScaling) {
    yAxis.set('min', customScaling![sensor.unit].min);
    yAxis.set('max', customScaling![sensor.unit].max);
    yAxis.set('strictMinMax', true);
    yAxis.get('renderer').setAll({ minGridDistance: 40 });
    return;
  }

  if (useStepLine) {
    yAxis.set('min', -0.5);
    yAxis.set('max', 1.5);
    yAxis.set('strictMinMax', true);
    yAxis.get('renderer').setAll({ minGridDistance: 50 });
    return;
  }

  const range = maxValue - minValue;
  const padding = range * 0.1;
  yAxis.set('min', minValue - padding);
  yAxis.set('max', maxValue + padding);
};

const setupIndividualChartCursor = (
  chart: am5xy.XYChart,
  root: am5.Root,
  xAxis: am5xy.DateAxis<am5xy.AxisRenderer>
): void => {
  const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {
    behavior: 'zoomXY',
    xAxis: xAxis
  }));

  cursor.selection.setAll({
    fill: am5.color(0x1fc9a7),
    fillOpacity: 0.2,
    stroke: am5.color(0x1fc9a7),
    strokeWidth: 2,
    strokeOpacity: 0.8
  });

  cursor.lineY.set('visible', false);
  cursor.lineX.setAll({
    visible: true,
    stroke: am5.color('#ADD8E6'),
    strokeWidth: 3,
  });

  let previousBulletSprites: any[] = [];
  const cursorMoved = () => {
    for (let i = 0; i < previousBulletSprites.length; i++) {
      previousBulletSprites[i]?.unhover();
    }
    previousBulletSprites = [];
    chart.series.each((seriesItem) => {
      const dataItem = seriesItem?.get('tooltip')?.dataItem;
      if (dataItem) {
        const bulletSprite = dataItem?.bullets?.length && dataItem?.bullets[0]?.get('sprite');
        if (bulletSprite) {
          bulletSprite.hover();
          previousBulletSprites.push(bulletSprite);
        }
      }
    });
  };

  cursor.events.on('cursormoved', cursorMoved);
};

interface CreateIndividualChartOptions {
  sensor: SensorData;
  index: number;
  totalCount: number;
  container: HTMLDivElement;
  customScaling?: IndividualChartProps['customScaling'];
  individualScaling: { [chartId: string]: { min: number; max: number } };
  onScaleIconClick: (sensor: SensorData) => void;
}

const createIndividualChart = ({
  sensor,
  index,
  totalCount,
  container,
  customScaling,
  individualScaling,
  onScaleIconClick
}: CreateIndividualChartOptions): am5xy.XYChart => {
  const wrapperDiv = document.createElement('div');
  wrapperDiv.className = 'chart-container-wrapper';
  wrapperDiv.style.position = 'relative';
  wrapperDiv.style.marginBottom = index < totalCount - 1 ? '20px' : '0';

  const chartDiv = document.createElement('div');
  chartDiv.id = `individual-chart-${sensor.id}`;
  chartDiv.style.width = '100%';
  chartDiv.style.height = '300px';

  const customScaleIcon = document.createElement('button');
  customScaleIcon.type = 'button';
  customScaleIcon.className = 'individual-custom-scale-icon';
  customScaleIcon.title = 'Custom scaling';

  const scaleImg = document.createElement('img');
  scaleImg.src = scaleIcon;
  scaleImg.alt = 'Custom scaling';
  customScaleIcon.appendChild(scaleImg);
  customScaleIcon.addEventListener('click', (e) => {
    e.stopPropagation();
    onScaleIconClick(sensor);
  });

  wrapperDiv.appendChild(chartDiv);
  wrapperDiv.appendChild(customScaleIcon);
  container.appendChild(wrapperDiv);

  const useStepLine = isRelaySensor(sensor);
  const root = am5.Root.new(chartDiv.id);
  root.setThemes([am5themes_Dark.new(root)]);

  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: 'panX',
      wheelY: 'zoomX',
      pinchZoomX: true,
      paddingLeft: 0,
      paddingRight: 40,
      layout: root.verticalLayout
    })
  );

  chart.zoomOutButton.setAll({
    x: am5.p100,
    centerX: am5.p100,
    y: 10
  });

  const xAxis = chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      baseInterval: { timeUnit: 'minute', count: 1 },
      renderer: am5xy.AxisRendererX.new(root, {
        minGridDistance: 20,
        minorGridEnabled: true
      }),
    })
  );

  const yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      min: customScaling?.[sensor.unit]?.min,
      max: customScaling?.[sensor.unit]?.max,
      strictMinMax: customScaling?.[sensor.unit] ? true : false,
      renderer: am5xy.AxisRendererY.new(root, {
        strokeOpacity: 0.1,
        minGridDistance: 40
      })
    })
  );

  xAxis.get('renderer').labels.template.setAll({
    fill: am5.color('#bfc9db'),
    fontSize: 12,
    rotation: -45,
    centerX: am5.p100,
    centerY: am5.p0
  });

  setupXAxisDayLabelColors(xAxis);

  yAxis.get('renderer').labels.template.setAll({
    fill: am5.color('#bfc9db'),
    fontSize: 12
  });

  chart.children.push(
    am5.Label.new(root, {
      text: `${sensor.sensorName} (${sensor.unit || ''})`,
      x: am5.percent(50),
      centerX: am5.percent(50),
      y: 10,
      fill: am5.color('#e0e0e0'),
      fontSize: 14,
      fontWeight: '600',
      paddingTop: -13
    })
  );

  const series = useStepLine ?
    chart.series.push(
      am5xy.StepLineSeries.new(root, {
        name: sensor.sensorName,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'value',
        valueXField: 'date',
        connect: false,
        stroke: am5.color(sensor.color),
        fill: am5.color(sensor.color),
        noRisers: false,
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'down',
          dy: -20,
          labelText: '{name}: {valueY} ' + (sensor.unit || '') + '\n{valueX.formatDate("dd-MM-yyyy hh:mm a")}'
        })
      })
    ) :
    chart.series.push(
      am5xy.LineSeries.new(root, {
        name: sensor.sensorName,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'value',
        valueXField: 'date',
        connect: false,
        stroke: am5.color(sensor.color),
        fill: am5.color(sensor.color),
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'down',
          dy: 120,
          labelText: '{name}: {valueY} ' + (sensor.unit || '') + '\n{valueX.formatDate("dd-MM-yyyy hh:mm a")}'
        })
      })
    );

  series.strokes.template.setAll({
    strokeWidth: useStepLine ? 3 : 2
  });

  series.fills.template.setAll({
    fillOpacity: useStepLine ? 100 : 0,
    visible: false
  });

  series.bullets.push(() => createSeriesBullet(root));

  const sortedData = sortSensorDataPoints(sensor.dataPoints);
  const processedData = processDataWithGaps(sortedData);
  series.data.setAll(processedData);

  xAxis.set('baseInterval', { timeUnit: 'minute', count: 5 });

  const cleanData = filterCleanChartData(sortedData);
  if (processedData.length === 0) {
    series.data.setAll(cleanData);
  }

  const thresholdValues = getThresholdValues(sensor);
  const dataValues = extractChartDataValues(cleanData);
  const allValues = [...dataValues, ...thresholdValues];
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  configureIndividualYAxis(
    yAxis,
    sensor,
    useStepLine,
    minValue,
    maxValue,
    individualScaling,
    customScaling
  );

  xAxis.data.setAll(cleanData);
  yAxis.get('renderer').setAll({ minGridDistance: 30 });

  if (sensor.alarmHHData && sensor.alarmHHData.length > 0) {
    addThresholdLine(yAxis, sensor.alarmHHData[0].value, 'HH', am5.color('#FF0000'));
  }
  if (sensor.alarmHData && sensor.alarmHData.length > 0) {
    addThresholdLine(yAxis, sensor.alarmHData[0].value, 'H', am5.color('#FF9900'));
  }
  if (sensor.alarmLData && sensor.alarmLData.length > 0) {
    addThresholdLine(yAxis, sensor.alarmLData[0].value, 'L', am5.color('#FF9900'));
  }
  if (sensor.alarmLLData && sensor.alarmLLData.length > 0) {
    addThresholdLine(yAxis, sensor.alarmLLData[0].value, 'LL', am5.color('#FF0000'));
  }

  setupIndividualChartCursor(chart, root, xAxis);

  series.appear(1000, 100);
  chart.appear(1000, 100);

  return chart;
};

const IndividualChart: React.FC<IndividualChartProps> = ({
  sensors,
  selectedSensors,
  selectedTimeRange,
  customScaling,
  uomRanges,
  hasNoData,
  initialDataLoadComplete,
  chartDataLoading,
  onCustomScalingOpen
}) => {
  const chartsContainerRef = useRef<HTMLDivElement>(null);
  const chartInstancesRef = useRef<am5xy.XYChart[]>([]);
  const [chartsCreated, setChartsCreated] = useState(false);
  const chartCreationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // State for individual chart scaling modals
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const scalingDialogRef = useRef<HTMLDialogElement>(null);
  const [individualScaling, setIndividualScaling] = useState<{
    [chartId: string]: { min: number; max: number }
  }>({});
  const [tempScalingValues, setTempScalingValues] = useState<{
    [chartId: string]: { min: string; max: string }
  }>({});
  
  // Get selected sensor data
  const getSelectedSensorData = () => {
    return sensors.filter(sensor => 
      selectedSensors.includes(sensor.channelId) &&
      sensor.dataPoints &&
      sensor.dataPoints.length > 0
    );
  };

  const selectedSensorData = getSelectedSensorData();

  const getMinMaxForUnit = (unit: string) => getMinMaxForUnitFromRanges(unit, uomRanges);

  const openScalingModal = (sensor: SensorData) => {
    const chartId = sensor.id;
    const hasIndividualScaling = individualScaling[chartId];
    const hasGlobalScaling = customScaling && customScaling[sensor.unit];

    let currentMin: number;
    let currentMax: number;

    if (hasIndividualScaling) {
      currentMin = individualScaling[chartId].min;
      currentMax = individualScaling[chartId].max;
    } else if (hasGlobalScaling) {
      currentMin = customScaling![sensor.unit].min;
      currentMax = customScaling![sensor.unit].max;
    } else {
      const uomMinMax = getMinMaxForUnit(sensor.unit || '');
      if (uomMinMax.min !== '' && uomMinMax.max !== '') {
        currentMin = parseFloat(uomMinMax.min.toString());
        currentMax = parseFloat(uomMinMax.max.toString());
      } else {
        const dataValues: number[] = [];
        for (let i = 0; i < sensor.dataPoints.length; i++) {
          dataValues.push(sensor.dataPoints[i].value);
        }
        const minValue = Math.min(...dataValues);
        const maxValue = Math.max(...dataValues);
        const range = maxValue - minValue;
        const padding = range * 0.1;
        currentMin = minValue - padding;
        currentMax = maxValue + padding;
      }
    }

    setTempScalingValues(prev => ({
      ...prev,
      [chartId]: {
        min: Math.round(currentMin).toString(),
        max: Math.round(currentMax).toString()
      }
    }));
    setActiveModal(chartId);
  };

  // Create individual charts
  useLayoutEffect(() => {
    if (chartCreationTimeoutRef.current) {
      clearTimeout(chartCreationTimeoutRef.current);
    }

    // Clear any existing chart timeout
    chartCreationTimeoutRef.current = setTimeout(() => {
      const existingCharts = chartInstancesRef.current;
      for (let i = 0; i < existingCharts.length; i++) {
        const chart = existingCharts[i];
        if (chart) {
          chart.root.dispose();
        }
      }
      chartInstancesRef.current = [];

      // Clear container
      if (chartsContainerRef.current) {
        chartsContainerRef.current.innerHTML = '';
      }

      if (!chartsContainerRef.current || selectedSensorData.length === 0 || chartDataLoading) {
        setChartsCreated(false);
        return;
      }

      try {
        for (let index = 0; index < selectedSensorData.length; index++) {
          const sensor = selectedSensorData[index];
          const chart = createIndividualChart({
            sensor,
            index,
            totalCount: selectedSensorData.length,
            container: chartsContainerRef.current!,
            customScaling,
            individualScaling,
            onScaleIconClick: openScalingModal
          });
          chartInstancesRef.current.push(chart);
        }

        setChartsCreated(true);
      } catch (error) {
        console.error('Error creating individual charts:', error);
        setChartsCreated(false);
      }
    }, 200);

    return () => {
      if (chartCreationTimeoutRef.current) {
        clearTimeout(chartCreationTimeoutRef.current);
      }
    };
  }, [selectedSensorData.length, selectedSensors, customScaling, individualScaling, chartDataLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      chartInstancesRef.current.forEach(chart => {
        if (chart) {
          chart.root.dispose();
        }
      });
      chartInstancesRef.current = [];
      if (chartCreationTimeoutRef.current) {
        clearTimeout(chartCreationTimeoutRef.current);
      }
    };
  }, []);

  // Handle visibility changes
  useEffect(() => {
    const handleResize = () => {
      chartInstancesRef.current.forEach(chart => {
        if (chart) {
          chart.root.resize();
        }
      });
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chartsCreated]);

  // Helper functions for individual modal management
  const handleIndividualScalingValueChange = (chartId: string, field: 'min' | 'max', value: string) => {
    setTempScalingValues(prev => ({
      ...prev,
      [chartId]: {
        ...prev[chartId],
        [field]: value
      }
    }));
  };

  const handleIndividualScalingApply = (chartId: string, sensorUnit: string) => {
    const tempValues = tempScalingValues[chartId];
    if (tempValues) {
      const minVal = parseFloat(tempValues.min);
      const maxVal = parseFloat(tempValues.max);
      
      // Validate values
      if (!isNaN(minVal) && !isNaN(maxVal) && minVal < maxVal) {
        // Set individual scaling for this specific chart
        setIndividualScaling(prev => ({
          ...prev,
          [chartId]: {
            min: minVal,
            max: maxVal
          }
        }));
        
        setActiveModal(null);
      }
    }
  };

  const handleIndividualScalingClose = () => {
    setActiveModal(null);
    setTempScalingValues({});
  };

  const isIndividualApplyButtonEnabled = (chartId: string) => {
    const tempValues = tempScalingValues[chartId];
    if (!tempValues) return false;
    
    const minVal = parseFloat(tempValues.min);
    const maxVal = parseFloat(tempValues.max);
    
    return !isNaN(minVal) && !isNaN(maxVal) && minVal < maxVal;
  };

  useEffect(() => {
    if (!activeModal) {
      setTempScalingValues({});
      return;
    }

    const dialog = scalingDialogRef.current;
    if (!dialog) return;

    dialog.showModal();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleCancel = (e: Event) => {
      e.preventDefault();
      handleIndividualScalingClose();
    };

    const handleBackdropClick = (e: MouseEvent) => {
      if (e.target === dialog) {
        handleIndividualScalingClose();
      }
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('click', handleBackdropClick);

    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('click', handleBackdropClick);
      dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, [activeModal]);

  return (
    <div className="individual-charts-container">
      {!initialDataLoadComplete ? (
        <div className="loading-message">
          <Loader />
        </div>
      ) : selectedSensors.length > 0 ? (
        <div style={{ position: 'relative', width: "100%" }}>
          {chartDataLoading && !chartsCreated && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(10, 25, 41, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: '8px',
              gap: '12px'
            }}>
              <Loader />
              <div style={{ color: '#bfc9db', fontSize: '16px' }}>
                Loading chart data for {selectedTimeRange.toLowerCase()}...
              </div>
            </div>
          )}
          {initialDataLoadComplete && hasNoData && !chartDataLoading && !chartsCreated && selectedSensorData.length === 0 ? (
            <div className="no-data-message">
              <p>No data found for the selected sensors in the {selectedTimeRange.toLowerCase()} time period.</p>
              <p style={{ fontSize: '14px', color: '#8b9cb8', marginTop: '8px' }}>
                Try selecting a different time range or different sensors.
              </p>
            </div>
          ) : (
            <div
              ref={chartsContainerRef}
              style={{
                width: "96%",
                minWidth: "800px",
                marginLeft:"8px",
                marginTop: "6px",
                marginBottom: "6px"
              }}
            />
          )}
        </div>
      ) : (
        <div className="no-data-message">
          <p>Please select at least one sensor to display the charts.</p>
        </div>
      )}
      
      {/* Individual Custom Scaling Modals */}
      {activeModal && (() => {
        const activeSensor = selectedSensorData.find(sensor => sensor.id === activeModal);
        if (!activeSensor) return null;
        
        return (
          <dialog
            ref={scalingDialogRef}
            className="individual-scaling-dialog"
            aria-labelledby="individual-scaling-title"
          >
            <div 
              className="custom-scaling-modal"
              style={{
                background: '#0a1929',
                border: '1px solid #4a5463',
                borderRadius: '8px',
                width: '270px',
                color: '#e0e0e0',
                marginTop: '50px',
                paddingLeft: '8px',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '8px',
                paddingBottom: '10px',
              }}>
                <h3
                  id="individual-scaling-title"
                  style={{
                  color: '#fff',
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: '0',
                  flexGrow: 1,
                  textAlign: 'center'
                }}>
                  Custom Scaling
                </h3>
                <button
                  onClick={handleIndividualScalingClose}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#e0e0e0',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    width: '28px',
                    height: '28px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#22314a';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#e0e0e0';
                  }}
                  title="Close"
                >
                  ✕
                </button>
              </div>
              
              <div style={{ 
                marginBottom: '24px', 
                maxHeight: '400px', 
                overflowY: 'auto'
              }}>
                {/* Axis Field */}
                <div style={{ marginBottom: '8px' }}>
                  <div style={{
                    color: '#bfc9db',
                    fontSize: '14px'
                  }}>
                    Axis : {activeSensor.sensorName} ({activeSensor.unit || 'N/A'})
                  </div>
                </div>
                
                {/* Min and Max Fields in a row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0px',
                  marginBottom: '16px'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      color: '#bfc9db',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      Min
                    </label>
                    <input
                      type="number"
                      value={tempScalingValues[activeModal]?.min || ''}
                      onChange={(e) => handleIndividualScalingValueChange(activeModal, 'min', e.target.value)}
                      placeholder="Min value"
                      style={{
                        width: '70%',
                        background: '#22314a',
                        color: '#e0e0e0',
                        border: '1px solid #4a5463',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{
                      display: 'block',
                      color: '#bfc9db',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      Max
                    </label>
                    <input
                      type="number"
                      value={tempScalingValues[activeModal]?.max || ''}
                      onChange={(e) => handleIndividualScalingValueChange(activeModal, 'max', e.target.value)}
                      placeholder="Max value"
                      style={{
                        width: '70%',
                        background: '#22314a',
                        color: '#e0e0e0',
                        border: '1px solid #4a5463',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '-20px',
                paddingBottom: '10px'
              }}>
                <button
                  onClick={handleIndividualScalingClose}
                  style={{
                    background: 'transparent',
                    color: '#e0e0e0',
                    border: '1px solid #4a5463',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#22314a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleIndividualScalingApply(activeModal, activeSensor.unit)}
                  disabled={!isIndividualApplyButtonEnabled(activeModal)}
                  style={{
                    background: isIndividualApplyButtonEnabled(activeModal) ? '#1ec9a7' : '#4a5463',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: isIndividualApplyButtonEnabled(activeModal) ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    marginRight: '14px'
                  }}
                  onMouseEnter={(e) => {
                    if (isIndividualApplyButtonEnabled(activeModal)) {
                      e.currentTarget.style.background = '#1ab396';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isIndividualApplyButtonEnabled(activeModal)) {
                      e.currentTarget.style.background = '#1ec9a7';
                    }
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </dialog>
        );
      })()}
    </div>
  );
};

export default IndividualChart;